import { GameResult, GameType } from '../types/game.types'

const STORAGE_KEY = 'game-results'
const MAX_RESULTS_PER_GAME = 5

export function saveGameResult(result: GameResult): void {
  const results = getGameResults()
  results.unshift(result)
  
  // Keep only last MAX_RESULTS_PER_GAME results per game type
  const filteredByType = results
    .filter(r => r.gameType === result.gameType)
    .slice(0, MAX_RESULTS_PER_GAME)
  
  const otherResults = results.filter(r => r.gameType !== result.gameType)
  const allResults = [...filteredByType, ...otherResults]
    .sort((a, b) => b.timestamp - a.timestamp) // Sort by most recent
    .slice(0, MAX_RESULTS_PER_GAME * 2) // Keep max 10 total results (5 per game type)
  
  localStorage.setItem(STORAGE_KEY, JSON.stringify(allResults))
}

export function getGameResults(gameType?: GameType): GameResult[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    const results: GameResult[] = stored ? JSON.parse(stored) : []
    
    // Validate results structure
    const validResults = results.filter(isValidGameResult)
    
    return gameType 
      ? validResults.filter(r => r.gameType === gameType)
      : validResults
  } catch (error) {
    console.error('Error reading game results from localStorage:', error)
    return []
  }
}

export function clearGameResults(gameType?: GameType): void {
  if (gameType) {
    const results = getGameResults()
    const filtered = results.filter(r => r.gameType !== gameType)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered))
  } else {
    localStorage.removeItem(STORAGE_KEY)
  }
}

export function getBestScore(gameType: GameType): number {
  const results = getGameResults(gameType)
  return results.length > 0 
    ? Math.max(...results.map(r => r.score))
    : 0
}

export function getGameStats(gameType: GameType) {
  const results = getGameResults(gameType)
  const totalGames = results.length
  const wins = results.filter(r => r.winner === 'player' || r.winner === 'X' || r.winner === 'O').length
  const losses = results.filter(r => r.winner === null && !r.winner).length
  const draws = results.filter(r => r.winner === 'draw').length
  const totalScore = results.reduce((sum, r) => sum + r.score, 0)
  const averageScore = totalGames > 0 ? totalScore / totalGames : 0
  const bestScore = getBestScore(gameType)
  const totalPlayTime = results.reduce((sum, r) => sum + r.duration, 0)
  
  return {
    totalGames,
    wins,
    losses,
    draws,
    averageScore,
    bestScore,
    totalPlayTime
  }
}

function isValidGameResult(result: any): result is GameResult {
  return (
    result &&
    typeof result.id === 'string' &&
    (result.gameType === 'tic-tac-toe' || result.gameType === '2048') &&
    typeof result.score === 'number' &&
    typeof result.duration === 'number' &&
    typeof result.timestamp === 'number' &&
    typeof result.moves === 'number'
  )
}

// Helper to generate unique ID
export function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substring(2)
}